import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/cliente';
import type { Archivo, Media } from '../api/tipos';
import { useSesion } from '../auth/sesion';
import { Aviso, Cargando, Tarjeta, Vacio } from '../ui/piezas';

/** La primera variante servible: el navegador entiende las tres. */
function miniatura(media: Media): string | null {
  const variante = media.variantes.card ?? media.variantes.thumb ?? media.variantes.medium;
  if (!variante) return null;
  return variante.webp ?? variante.jpg ?? variante.avif ?? null;
}

export default function Galeria({ inmuebleId }: { inmuebleId: string }) {
  const cache = useQueryClient();
  const { puede } = useSesion();
  const entrada = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<unknown>(null);
  const [subiendo, setSubiendo] = useState(false);

  const galeria = useQuery({
    queryKey: ['galeria', inmuebleId],
    queryFn: () => api<Media[]>(`/admin/inmuebles/${inmuebleId}/media`),
    // Las imágenes se procesan en segundo plano: mientras alguna siga en la
    // cola se vuelve a preguntar, en vez de dejar un hueco gris para siempre.
    // Las que fallaron no cuentan: ya no van a cambiar, y preguntar por ellas
    // dejaba el panel repreguntando indefinidamente.
    refetchInterval: (consultaActual) =>
      (consultaActual.state.data ?? []).some(
        (m) => m.estado === 'PENDIENTE' || m.estado === 'PROCESANDO',
      )
        ? 4000
        : false,
  });

  const refrescar = () => void cache.invalidateQueries({ queryKey: ['galeria', inmuebleId] });

  const subir = async (archivos: FileList | null) => {
    if (!archivos || archivos.length === 0) return;
    setError(null);
    setSubiendo(true);
    try {
      // De una en una y no en paralelo: media docena de fotos de 6 MB a la vez
      // desde la oficina satura la subida y el navegador empieza a abortar.
      for (const archivo of Array.from(archivos)) {
        const formulario = new FormData();
        formulario.append('archivo', archivo);
        const subido = await api<Archivo>('/admin/archivos?tipo=IMAGEN', {
          metodo: 'POST',
          formulario,
        });
        await api<Media>(`/admin/inmuebles/${inmuebleId}/media`, {
          metodo: 'POST',
          cuerpo: { archivoId: subido.id, rol: 'GALERIA' },
        });
      }
      refrescar();
    } catch (e) {
      setError(e);
    } finally {
      setSubiendo(false);
      if (entrada.current) entrada.current.value = '';
    }
  };

  const quitar = useMutation({
    mutationFn: (asociacionId: string) =>
      api<void>(`/admin/inmuebles/${inmuebleId}/media/${asociacionId}`, { metodo: 'DELETE' }),
    onSuccess: refrescar,
    onError: setError,
  });

  const hacerPortada = useMutation({
    mutationFn: (archivoId: string) =>
      api<Media>(`/admin/inmuebles/${inmuebleId}/media`, {
        metodo: 'POST',
        cuerpo: { archivoId, rol: 'PORTADA' },
      }),
    onSuccess: refrescar,
    onError: setError,
  });

  const reordenar = useMutation({
    mutationFn: (orden: string[]) =>
      api<Media[]>(`/admin/inmuebles/${inmuebleId}/media/orden`, {
        metodo: 'PUT',
        cuerpo: orden,
      }),
    onSuccess: refrescar,
    onError: setError,
  });

  const fotos = galeria.data ?? [];
  const editable = puede('inmueble:editar') && puede('multimedia:subir');

  /** Mandar la lista completa es idempotente: no depende de qué había antes. */
  const mover = (desde: number, hacia: number) => {
    if (hacia < 0 || hacia >= fotos.length) return;
    const orden = fotos.map((f) => f.asociacionId);
    const sacado = orden[desde];
    if (sacado === undefined) return;
    orden.splice(desde, 1);
    orden.splice(hacia, 0, sacado);
    reordenar.mutate(orden);
  };

  return (
    <Tarjeta titulo="Fotos">
      <Aviso error={error} />

      {editable && (
        <div className="fila" style={{ marginBottom: 14 }}>
          <input
            ref={entrada}
            type="file"
            accept="image/*"
            multiple
            style={{ maxWidth: 320 }}
            onChange={(e) => void subir(e.target.files)}
            disabled={subiendo}
          />
          {subiendo && <span className="campo__ayuda">Subiendo…</span>}
        </div>
      )}

      {galeria.isPending ? (
        <Cargando />
      ) : fotos.length === 0 ? (
        <Vacio>
          Sin fotos. En un portal inmobiliario la fotografía es el producto: una ficha sin
          imágenes no la abre nadie.
        </Vacio>
      ) : (
        <div className="galeria">
          {fotos.map((foto, indice) => {
            const url = miniatura(foto);
            return (
              <figure key={foto.asociacionId}>
                {url ? (
                  <img src={url} alt={foto.alt ?? ''} loading="lazy" />
                ) : (
                  <div
                    style={{
                      aspectRatio: '4 / 3',
                      display: 'grid',
                      placeItems: 'center',
                      padding: 10,
                      textAlign: 'center',
                      fontSize: '0.78rem',
                      color: foto.estado === 'ERROR' ? 'var(--mal)' : 'var(--texto-2)',
                    }}
                  >
                    {foto.estado === 'ERROR'
                      ? (foto.errorMensaje ?? 'No se pudo procesar. Súbela de nuevo.')
                      : 'Procesando…'}
                  </div>
                )}
                <figcaption>
                  <span>
                    {foto.rol === 'PORTADA' ? 'Portada' : `#${indice + 1}`}
                    {foto.estado === 'ERROR' && ' · falló'}
                  </span>
                  {editable && (
                    <span>
                      <button className="plano" onClick={() => mover(indice, indice - 1)}>
                        ←
                      </button>
                      <button className="plano" onClick={() => mover(indice, indice + 1)}>
                        →
                      </button>
                      {foto.rol !== 'PORTADA' && (
                        <button
                          className="plano"
                          title="Poner como portada"
                          onClick={() => hacerPortada.mutate(foto.archivoId)}
                        >
                          ★
                        </button>
                      )}
                      <button
                        className="plano"
                        title="Quitar"
                        onClick={() => quitar.mutate(foto.asociacionId)}
                      >
                        ✕
                      </button>
                    </span>
                  )}
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}

      <p className="campo__ayuda" style={{ marginTop: 12 }}>
        Quitar una foto de aquí no borra el archivo: sigue disponible por si se asocia a otro
        inmueble.
      </p>
    </Tarjeta>
  );
}
